import { useEffect, useState } from 'react';

interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Vérifier le support
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      // Vérifier la permission actuelle
      setPermission(Notification.permission);

      // Vérifier l'abonnement existant
      checkExistingSubscription();
    }
  }, []);

  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      setSubscription(existingSubscription);
      setIsSubscribed(!!existingSubscription);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      return permissionResult === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const subscribeToPush = async (): Promise<boolean> => {
    if (!isSupported || permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Récupérer la clé publique depuis le backend
      const response = await fetch('/api/pwa/vapid-public-key');
      const { vapidPublicKey } = await response.json();

      const keyArray = urlBase64ToUint8Array(vapidPublicKey);
      const applicationServerKey = new Uint8Array(keyArray);
      
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer
      });

      // Envoyer l'abonnement au backend
      await sendSubscriptionToBackend(pushSubscription);

      setSubscription(pushSubscription);
      setIsSubscribed(true);

      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return false;
    }
  };

  const unsubscribeFromPush = async (): Promise<boolean> => {
    if (!subscription) return true;

    try {
      await subscription.unsubscribe();
      
      // Supprimer l'abonnement du backend
      await removeSubscriptionFromBackend(subscription);

      setSubscription(null);
      setIsSubscribed(false);

      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  };

  const sendSubscriptionToBackend = async (subscription: PushSubscription) => {
    try {
      await fetch('/api/pwa/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: subscription.getKey('p256dh') 
              ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!)))
              : '',
            auth: subscription.getKey('auth')
              ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))
              : ''
          }
        })
      });
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
    }
  };

  const removeSubscriptionFromBackend = async (subscription: PushSubscription) => {
    try {
      await fetch('/api/pwa/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
    }
  };

  // Utilitaire pour convertir la clé VAPID
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    checkExistingSubscription
  };
}
